import React, { useState } from 'react';
import { IonButton, IonInput, IonItem, IonLabel, IonModal, IonTextarea } from '@ionic/react';
import { ServiceEstimate, ServiceSettingsBundle } from '../../../interfaces/ServiceEstimate';
import { serviceApi } from '../../../utils/serviceEstimatesApi';
import AcceptancePanel from './AcceptancePanel';
import { money, shortDate } from './serviceFormat';
import logo from '../../../AE2/static/pics/alpha-controls-logo.jpg';
import './ProposalView.css';

interface Props {
  bundle: ServiceSettingsBundle;
  /** The saved estimate. The proposal always shows what is saved, never unsaved edits. */
  estimate: ServiceEstimate | null;
  /** Unsaved edits on the Estimate tab. */
  dirty: boolean;
  onSaved: (e: ServiceEstimate) => void;
  notify: (msg: string) => void;
  onGoEstimate: () => void;
}

const DEFAULT_CLARIFICATIONS = [
  'Defective equipment not listed in the above scope.  A list will be provided at completion of agreed work.',
  'Fire and/or fire/smoke dampers, including life safety products.',
  'Patching and /or painting.',
  'Professional services including A/E fees, ACM abatement, Bonding or Permits.',
];
const DEFAULT_OFFICES = [
  { name: 'Corporate HQ:', line1: '4104 Charles Street', line2: 'Rockford, IL 61108' },
  { name: 'Springfield Office:', line1: '2867 Via Verde', line2: 'Springfield, IL 62703' },
  { name: 'Champaign Office:', line1: '2110 Clearlake Boulevard Suite #101', line2: 'Champaign, IL 61822' },
  { name: 'Wisconsin Office:', line1: '8845 S. Greenview Drive #2', line2: 'Middleton, WI 53562' },
];

/** The Proposal tab (2.0): an on-screen copy of the proposal, the PDF and email, and the customer's signature. */
const ProposalView: React.FC<Props> = ({ bundle, estimate, dirty, onSaved, notify, onGoEstimate }) => {
  const [emailOpen, setEmailOpen] = useState(false);
  const [email, setEmail] = useState({ to: '', cc: '', message: '' });
  const [busy, setBusy] = useState(false);

  if (!estimate) {
    return (
      <div className="proposal-view">
        <div className="proposal-note flag">
          <span>Save the estimate to see the proposal here.</span>
          <IonButton fill="outline" onClick={onGoEstimate}>Go to the estimate</IonButton>
        </div>
      </div>
    );
  }

  const e = estimate;
  const closed = e.status === 'accepted' || e.status === 'declined';
  const issued = (e.status === 'ready' || e.status === 'sent') && !e.is_expired;
  const pdfOk = !dirty && (issued || e.status === 'accepted');
  let note: string;
  if (e.status === 'accepted') note = 'Accepted and signed. Download or email the signed PDF for the customer’s copy.';
  else if (e.status === 'declined') note = 'The customer declined this proposal.';
  else if (dirty) note = 'You have unsaved changes on the Estimate tab. This shows the last saved version; save there first.';
  else if (e.status === 'submitted') note = e.changed_after_acs
    ? `Preview only. The office is rechecking the changes to ${e.acs_number}; the PDF and signature unlock when it's done.`
    : 'Preview only. The PDF and signature unlock once the office assigns the ACS#.';
  else if (e.status === 'draft') note = 'Preview only. Submit the estimate for an ACS# on the Estimate tab; the PDF and signature need it.';
  else if (e.is_expired) note = 'This proposal has expired. Change the date on the Estimate tab and resubmit.';
  else note = 'Ready to send. Download or email the PDF, or have the customer sign below.';
  const ok = closed || (pdfOk && issued);

  const tech = bundle.technicians?.find((t) => t.id === e.prepared_by);
  const clar = bundle.settings.clarifications?.length ? bundle.settings.clarifications : DEFAULT_CLARIFICATIONS;
  const offices = bundle.settings.offices?.length ? bundle.settings.offices : DEFAULT_OFFICES;
  const assumptions = [...e.assumptions.filter((a) => a.trim()), `This proposal is valid for ${e.valid_days} days, until ${shortDate(e.valid_until)}.`];
  const signed = e.status === 'accepted';
  const left = [['Name', e.prepared_by_name], ['Title', tech?.title || 'Technician'], ['Company', 'Alpha Controls & Services'], ['Date', shortDate(e.estimate_date)]];
  const right = [['Name', signed ? e.accepted_name : ''], ['Title', signed ? e.accepted_title : ''], ['Company', signed ? e.customer_detail.name : ''], ['Date', signed ? shortDate(e.accepted_at) : '']];

  const openPdf = async () => {
    setBusy(true);
    try {
      const blob = await serviceApi.proposalPdf(e.id);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank'); // print or save from the PDF viewer
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      onSaved(await serviceApi.getEstimate(e.id));
    } catch (err) {
      notify(err instanceof Error ? err.message : 'Could not make the PDF.');
    } finally {
      setBusy(false);
    }
  };

  const sendEmail = async () => {
    try {
      const r = await serviceApi.emailProposal(e.id, email.to, email.cc, email.message);
      setEmailOpen(false);
      notify(r.detail);
      onSaved(await serviceApi.getEstimate(e.id));
    } catch (err) {
      notify(err instanceof Error ? err.message : 'Could not send the email.');
    }
  };

  return (
    <div className="proposal-view">
      <div className={`proposal-note ${ok ? 'ok' : 'flag'}`}>
        <span>{note}</span>
        <div className="proposal-actions">
          {dirty && <IonButton fill="outline" onClick={onGoEstimate}>Back to the estimate</IonButton>}
          <IonButton onClick={openPdf} disabled={!pdfOk || busy}>{signed ? 'Signed PDF' : 'Proposal PDF'}</IonButton>
          <IonButton fill="outline" disabled={!pdfOk || busy}
            onClick={() => { setEmail({ to: e.contact_email || e.customer_detail.contact_email, cc: '', message: '' }); setEmailOpen(true); }}>
            Email to customer
          </IonButton>
        </div>
      </div>

      <div className="proposal-scroll">
        <article className="proposal-sheet" aria-label="Proposal preview">
          <div className="proposal-head">
            <div className="proposal-title">PROPOSAL</div>
            <img src={logo} alt="Alpha Controls &amp; Services" />
          </div>
          <div className="proposal-two">
            <div>
              <div className="proposal-label">Proposed By</div>
              <div className="proposal-ul">{e.prepared_by_name}</div>
              <div>{tech?.title || 'Technician'}</div>
              {tech?.email && <div>E: {tech.email}</div>}
              {tech?.phone && <div>P: {tech.phone}</div>}
            </div>
            <div>
              <div className="proposal-label">Proposal For</div>
              <div className="proposal-ul">{e.customer_detail.name}</div>
              <div>Attn: {e.contact_name}</div>
              <div>Date: {shortDate(e.estimate_date)}</div>
              <div>Proposal #: {e.acs_number || 'pending'}</div>
              <div>Valid until: {shortDate(e.valid_until)}</div>
            </div>
          </div>
          <div className="proposal-sh">Scope of Work</div>
          <div className="proposal-scope">{e.scope_of_work}</div>
          <div className="proposal-total">Total Price: {money(e.total_price)}</div>
          <div className="proposal-sh">Assumptions:</div>
          <ul className="proposal-list">{assumptions.map((a, i) => <li key={i}>{a}</li>)}</ul>
          <div className="proposal-sh">Clarifications:</div>
          <ul className="proposal-list">{clar.map((a, i) => <li key={i}>{a}</li>)}</ul>
          <div className="proposal-two proposal-sign">
            <div>
              <div className="proposal-label">Proposed by:</div>
              {signed && e.signature_url && <div className="proposal-sigspace" />}
              {left.map(([k, v]) => <div className="proposal-row" key={k}><span>{k}</span><span className="proposal-ul">{v}</span></div>)}
            </div>
            <div>
              <div className="proposal-label">Accepted By</div>
              {signed && e.signature_url && <div className="proposal-row"><span /><span className="proposal-ul"><img src={e.signature_url} alt="Customer signature" className="proposal-sig" /></span></div>}
              {right.map(([k, v]) => <div className="proposal-row" key={k}><span>{k}</span><span className="proposal-ul">{v}</span></div>)}
            </div>
          </div>
          <div className="proposal-offices">
            {offices.slice(0, 4).map((o) => <div key={o.name}>{o.name}<br />{o.line1}<br />{o.line2}</div>)}
          </div>
        </article>
      </div>

      {((issued && !dirty) || closed) && <AcceptancePanel estimate={e} onDone={onSaved} notify={notify} />}

      <IonModal isOpen={emailOpen} onDidDismiss={() => setEmailOpen(false)}>
        <div className="estimate-email">
          <h2>Email the proposal</h2>
          <IonItem><IonInput label="To" labelPlacement="stacked" type="email" value={email.to} onIonInput={(ev) => setEmail({ ...email, to: String(ev.detail.value ?? '') })} /></IonItem>
          <IonItem><IonInput label="Cc (optional)" labelPlacement="stacked" value={email.cc} onIonInput={(ev) => setEmail({ ...email, cc: String(ev.detail.value ?? '') })} /></IonItem>
          <IonItem><IonTextarea label="Message (optional)" labelPlacement="stacked" autoGrow value={email.message} onIonInput={(ev) => setEmail({ ...email, message: String(ev.detail.value ?? '') })} /></IonItem>
          <IonLabel className="estimate-help">The PDF is attached. The customer can reply to accept, or sign with you on site.</IonLabel>
          <div className="estimate-actions">
            <IonButton onClick={sendEmail}>Send</IonButton>
            <IonButton fill="outline" onClick={() => setEmailOpen(false)}>Cancel</IonButton>
          </div>
        </div>
      </IonModal>
    </div>
  );
};

export default ProposalView;
